import React, { useCallback } from "react";
import { StyleSheet, View } from "react-native";
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useAuth } from "../hooks/useAuth";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import { useAppConfig } from "../AppConfigProvider";
import Loader from "../components/common/status/Loader/Loader";
import { getDefaultAuthenticatedRoute } from "../utils/authRoutes.utils";

const noAuth = (WrappedComponent) => {
  const NoAuthComponent = (props) => {
    const { isUpdating, isAuthenticated, isEmailVerified, otpVerifiedForSession, userInfo, isAuthReady } = useAuth();
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute();
    const { isCompanionMode, allowFormSignup } = useAppConfig();
    const isVerified = isEmailVerified && otpVerifiedForSession;

    useFocusEffect(
      useCallback(() => {
        if (!isAuthReady) {
          return;
        }

        if (!allowFormSignup && !isAuthenticated && route.name === "Signup") {
          navigation.reset({
            index: 0,
            routes: [{ name: "Login" }],
          });
          return;
        }

        if (route.name === "Verify Email" && !isAuthenticated) {
          navigation.reset({
            index: 0,
            routes: [{ name: "Login" }],
          });
          return;
        }

        if (isAuthenticated && !isVerified && route.name !== "Verify Email") {
          navigation.reset({
            index: 0,
            routes: [{ name: "Verify Email" }],
          });
          return;
        }

        if (isAuthenticated && isVerified) {
          const redirect = getDefaultAuthenticatedRoute({ userRole: userInfo.userRole, isCompanionMode });
          navigation.reset({
            index: 0,
            routes: [{ name: redirect }],
          });
        }
      }, [
        allowFormSignup,
        isAuthenticated,
        isAuthReady,
        isCompanionMode,
        isVerified,
        navigation,
        route.name,
        userInfo.userRole,
      ])
    );

    if (!isAuthReady || isUpdating) {
      return (
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      );
    }

    return <WrappedComponent {...props} />;
  };

  return NoAuthComponent;
};

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default noAuth;
